import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiShield, FiBrain, FiHeart, FiUsers, FiSmile, FiArrowRight } = FiIcons;

const Benefits = () => {
  const benefits = [
    {
      title: "Stop Fighting Cravings, Start Controlling Them",
      description: "With EF therapy: 50% increase in smoking cessation success and 25-70% decrease in relapse rates. Build the brain skills that make \"no\" feel natural.",
      icon: FiShield,
      color: "from-accent-magenta to-accent-purple"
    },
    {
      title: "Think Clearly for the First Time in Years",
      description: "Experience sharper focus, better decision-making, and the ability to plan ahead. Your brain fog lifts when your executive functions strengthen.",
      icon: FiBrain,
      color: "from-accent-gold to-accent-yellow"
    },
    {
      title: "Handle Stress Healthily",
      description: "Develop healthy coping strategies that actually work. Build emotional resilience that doesn't depend on substances.",
      icon: FiHeart,
      color: "from-green-500 to-emerald-500"
    },
    {
      title: "Repair Relationships and Rebuild Trust",
      description: "When you can consistently follow through on commitments, people notice. Executive function skills improve reliability and communication.",
      icon: FiUsers,
      color: "from-blue-500 to-cyan-500"
    },
    {
      title: "Actually Enjoy Being Sober",
      description: "Transform sobriety from white-knuckle endurance to genuine contentment. Build a life you don't want to escape from.",
      icon: FiSmile,
      color: "from-purple-500 to-pink-500"
    }
  ];

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section className="py-20 bg-gradient-to-br from-accent-light via-white to-accent-purple/10">
      <div className="max-w-6xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl lg:text-5xl font-playfair font-bold text-gray-900 mb-6">
            What Real Recovery{' '}
            <span className="bg-gradient-to-r from-accent-magenta to-accent-purple bg-clip-text text-transparent">
              Actually Feels Like
            </span>
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-8 mb-16">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ scale: 1.02 }}
              className="bg-white rounded-3xl p-8 shadow-lg hover:shadow-xl transition-all duration-300"
            >
              <div className={`w-16 h-16 bg-gradient-to-r ${benefit.color} rounded-2xl flex items-center justify-center mb-6`}>
                <SafeIcon icon={benefit.icon} className="w-8 h-8 text-white" />
              </div>
              
              <h3 className="text-xl lg:text-2xl font-playfair font-bold text-gray-900 mb-4">
                {benefit.title}
              </h3>
              
              <p className="text-gray-600 leading-relaxed">
                {benefit.description}
              </p>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <motion.button
            onClick={() => scrollToSection('contact')}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="bg-accent-magenta text-white px-8 py-4 rounded-full font-semibold text-lg flex items-center justify-center gap-2 mx-auto hover:bg-accent-magenta/90 transition-colors duration-200"
          >
            Start Your Recovery Transformation
            <SafeIcon icon={FiArrowRight} className="w-5 h-5" />
          </motion.button>
        </motion.div>
      </div>
    </section>
  );
};

export default Benefits;